import React, { useEffect, useState } from "react";
import { useIdleTimer } from 'react-idle-timer';
import { setLoginDetails, setPermissionDetails } from "../../action/set-action-details";
import { connect } from "react-redux";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { GeneralMobileHeader } from "./general-mobile-header";
import { Link } from "react-router-dom";
import Logo from "../../assets/img/logoo-.png";
import {
    Bars3Icon,
    ArrowRightOnRectangleIcon,
    SunIcon,
    MoonIcon,
    UserCircleIcon
} from "@heroicons/react/24/outline";

function AppHeader(props) {
    const [isOpen, setIsOpen] = useState(false);
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme) {
            return savedTheme;
        }
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    });

    const toggleDrawer = () => {
        setIsOpen((prevState) => !prevState);
    };

    useEffect(() => {
        // Initialize theme on mount
        const root = document.documentElement;
        if (theme === "dark") {
            root.classList.add("dark");
        } else {
            root.classList.remove("dark");
        }
        localStorage.setItem("theme", theme);
    }, []);

    useEffect(() => {
        const handleThemeChange = () => {
            const currentTheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
            setTheme(currentTheme);
        };
        window.addEventListener("themechange", handleThemeChange);
        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => {
            window.removeEventListener("themechange", handleThemeChange);
            observer.disconnect();
        };
    }, []);

    const SESSION_IDLE_MINUTES = 120;
    const handleOnIdle = (event) => {
        console.log('last active', getLastActiveTime())
        signOut();
    }

    const { getLastActiveTime } = useIdleTimer({
        timeout: 1000 * 60 * SESSION_IDLE_MINUTES,
        onIdle: handleOnIdle,
        debounce: 500,
    });


    const signOut = () => {
        props.setOnLoginDetails([]);
        props.setOnPermissionDetails([]);
    };

    return (
        <header
            id="kt_header"
            className="print:hidden sticky top-0 z-50 bg-slate-900 dark:bg-slate-900 border-b border-slate-800 dark:border-slate-700 shadow-sm transition-all duration-300"
        >
            {/* Main Header Bar */}
            <div className="bg-slate-900 dark:bg-slate-900 border-b border-slate-800 dark:border-slate-700">
                <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 lg:h-18">
                        {/* Left Section: Logo & Mobile Menu */}
                        <div className="flex items-center flex-1 min-w-0">
                            {/* Mobile Menu Toggle */}
                            <button
                                onClick={toggleDrawer}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleDrawer();
                                    }
                                }}
                                className="lg:hidden p-2.5 rounded-xl text-slate-200 hover:bg-slate-800 active:bg-slate-700 transition-all duration-200 mr-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                                id="kt_header_navs_toggle"
                                aria-label="Toggle menu"
                                aria-expanded={isOpen}
                                aria-controls="mobile-menu"
                            >
                                <Bars3Icon className="w-6 h-6" aria-hidden="true" />
                            </button>

                            {/* Logo */}
                            <Link
                                to={"/"}
                                className="flex items-center flex-shrink-0 group"
                            >
                                <img
                                    alt="Logo"
                                    src={Logo}
                                    style={{ height: '60px' }}
                                    className="h-8 sm:h-9 lg:h-10 transition-transform duration-200 group-hover:scale-105"
                                />
                            </Link>
                        </div>

                        {/* Right Section: Actions & User */}
                        <div className="flex items-center gap-2 lg:gap-3">
                            {/* Theme Toggle */}
                            <button
                                onClick={() => {
                                    const newTheme = theme === "light" ? "dark" : "light";
                                    document.documentElement.classList.toggle("dark");
                                    localStorage.setItem("theme", newTheme);
                                    setTheme(newTheme);
                                    window.dispatchEvent(new Event("themechange"));
                                }}
                                className="relative p-2.5 rounded-xl text-slate-200 hover:bg-slate-800 active:bg-slate-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 touch-manipulation group"
                                aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
                                title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
                            >
                                <div className="absolute inset-0 rounded-xl bg-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                {theme === "light" ? (
                                    <MoonIcon className="h-5 w-5 relative z-10 text-slate-200" aria-hidden="true" />
                                ) : (
                                    <SunIcon className="h-5 w-5 relative z-10 text-slate-200" aria-hidden="true" />
                                )}
                            </button>

                            {/* User Profile Section - Desktop */}
                            <div className="hidden md:flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-800 transition-all duration-200 group">
                                <Link
                                    to={"/staff/profile"}
                                    className="flex items-center gap-3"
                                >
                                    <div className="hidden lg:flex flex-col items-end">
                                        <span className="text-slate-400 text-xs font-medium leading-tight">Welcome back,</span>
                                        <span className="text-slate-100 text-sm font-semibold leading-tight">
                                            {props.loginData[0]?.full_name || 'User'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-indigo-600/20 backdrop-blur-sm border border-indigo-500/30 group-hover:bg-indigo-600/30 transition-all duration-200">
                                        <UserCircleIcon className="w-6 h-6 text-slate-200" />
                                    </div>
                                </Link>
                            </div>

                            {/* Sign Out Button */}
                            <button
                                onClick={signOut}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        signOut();
                                    }
                                }}
                                className="p-2.5 rounded-xl text-slate-200 hover:bg-red-600/20 active:bg-red-600/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 group"
                                title="Sign out"
                                aria-label="Sign out"
                            >
                                <ArrowRightOnRectangleIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar Drawer */}
            <Drawer open={isOpen} onClose={toggleDrawer} direction="left">
                <GeneralMobileHeader permissionData={props.permissionData} onClose={setIsOpen} />
            </Drawer>
        </header>
    )
}

const mapStateToProps = (state) => {
    return {
        loginData: state.LoginDetails,
        permissionData: state.PermissionDetails,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setOnLoginDetails: (p) => {
            dispatch(setLoginDetails(p));
        },
        setOnPermissionDetails: (p) => {
            dispatch(setPermissionDetails(p));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader);